import { LogLevel, type Configuration } from "@azure/msal-browser";
import * as Sentry from "@sentry/react";
import { IS_PROD } from "@/constants/Dev.constant";

const msalLogLevelToSentry = (level: LogLevel) => {
  switch (level) {
    case LogLevel.Error:
      return "error" as const;
    case LogLevel.Warning:
      return "warning" as const;
    default:
      return "info" as const;
  }
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_B2C_CLIENT_ID,
    authority: `https://${import.meta.env.VITE_B2C_AUTHORITY_HOST}/${import.meta.env.VITE_B2C_TENANT_ID}`,
    knownAuthorities: [import.meta.env.VITE_B2C_AUTHORITY_HOST],
    redirectUri: import.meta.env.VITE_B2C_REDIRECT_URI,
    postLogoutRedirectUri: "/"
  },
  cache: {
    cacheLocation: "localStorage"
  },
  system: {
    loggerOptions: {
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean
      ) => {
        if (containsPii) return;

        if (IS_PROD) {
          Sentry.addBreadcrumb({
            category: "msal",
            message,
            level: msalLogLevelToSentry(level)
          });
          return;
        }

        switch (level) {
          case LogLevel.Error:
            console.error("[msal]", message);
            return;
          case LogLevel.Warning:
            console.warn("[msal]", message);
            return;
          case LogLevel.Info:
            console.info("[msal]", message);
            return;
          case LogLevel.Verbose:
            console.debug("[msal]", message);
            return;
        }
      },
      logLevel: IS_PROD ? LogLevel.Warning : LogLevel.Verbose,
      piiLoggingEnabled: false
    }
  }
};

/**
 * Scopes to request use of the Backend Applications.
 * - import.meta.env.VITE_B2C_BE_SCOPE: Scope for the BE API.
 * - "User.Read": Scope for reading user information.
 */
export const scopes: string[] = [
  import.meta.env.VITE_B2C_BE_SCOPE,
  "User.Read"
];
